import React, { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  X, AlertTriangle, CheckCircle2, Printer, Activity, FlaskConical,
  ChevronRight, Loader2, Sparkles, CalendarClock,
} from "lucide-react";
import { api, fmtDate } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import UrgentActionsReport from "./UrgentActionsReport";

/**
 * Top-N urgent issues checklist — pops once per day on login (parent owns
 * the daily-pop logic) AND lives as a permanent card on the Squad Overview
 * via the same component (when opened via the dashboard, just doesn't auto-
 * dismiss the once-a-day cookie).
 *
 * Each item shows the athlete, the flagged marker (or active injury), the
 * last 2 values, the reference range, and a templated corrective action.
 * Coaches tick "Reviewed" to hide the item; it auto-resurfaces if the
 * underlying lab/injury changes (latest_source_id flip).
 */
export default function UrgentIssuesChecklist({ open, onClose, onPrintClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [showPrint, setShowPrint] = useState(false);

  useEscClose(open, () => { if (!savingKey) onClose?.(); });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/squad/urgent-issues?limit=10&include_reviewed=true");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load urgent issues");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggleReview = async (item) => {
    setSavingKey(item.issue_key);
    try {
      await api.post("/squad/urgent-issues/review", {
        issue_key: item.issue_key,
        latest_source_id: item.latest_source_id,
        reviewed: !item.reviewed,
      });
      // Optimistic local update so the tick is instant.
      setData((d) => ({
        ...d,
        items: d.items.map((it) =>
          it.issue_key === item.issue_key ? { ...it, reviewed: !it.reviewed } : it,
        ),
      }));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not save review state");
    } finally {
      setSavingKey(null);
    }
  };

  const unreviewedCount = useMemo(
    () => (data?.items || []).filter((it) => !it.reviewed).length,
    [data],
  );

  if (!open) return null;

  // Print view takes over the modal — keeps the data already loaded.
  if (showPrint) {
    return (
      <UrgentActionsReport
        items={(data?.items || []).filter((it) => !it.reviewed)}
        generatedAt={data?.generated_at}
        onClose={() => {
          setShowPrint(false);
          onPrintClose?.();
        }}
      />
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[195] bg-[#040D1A]/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-3"
      data-testid="urgent-issues-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="card w-full max-w-5xl p-5 md:p-6">
        <Header
          unreviewedCount={unreviewedCount}
          totalItems={data?.items?.length || 0}
          onPrint={() => setShowPrint(true)}
          onClose={onClose}
        />

        {error && (
          <div className="mt-3 px-3 py-2 rounded-md border border-rose-500/50 bg-rose-500/10 text-rose-200 text-[12px]" data-testid="urgent-error">
            <AlertTriangle size={12} className="inline mr-1.5" /> {error}
          </div>
        )}

        {loading && (
          <div className="py-14 text-center text-slate-400">
            <Loader2 className="animate-spin inline mr-2" size={16} /> Scanning the squad…
          </div>
        )}

        {!loading && data && (data.items || []).length === 0 && (
          <div className="py-14 text-center text-emerald-300" data-testid="urgent-empty">
            <CheckCircle2 className="mx-auto mb-3" size={36} />
            <div className="font-display text-base">All clear.</div>
            <div className="text-[12px] text-slate-500 mt-1">
              No persistent + alarming issues across the squad today.
            </div>
          </div>
        )}

        {!loading && data && (data.items || []).length > 0 && (
          <div className="mt-4 space-y-2" data-testid="urgent-list">
            {data.items.map((it, idx) => (
              <UrgentRow
                key={it.issue_key}
                rank={idx + 1}
                item={it}
                onToggle={() => toggleReview(it)}
                saving={savingKey === it.issue_key}
              />
            ))}
            {data.total_candidates > data.items.length && (
              <div className="text-[11px] text-slate-500 text-center pt-2">
                Showing top {data.items.length} of {data.total_candidates} candidates.
                Thresholds: ≥{data.thresholds.persistence_min} consecutive abnormal labs,
                &gt; {data.thresholds.deviation_pct_min}% out of range.
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function Header({ unreviewedCount, totalItems, onPrint, onClose }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-rose-300/90 flex items-center gap-1.5">
          <AlertTriangle size={11} /> Urgent corrective actions
        </div>
        <div className="font-display text-lg md:text-xl text-slate-100 mt-0.5">
          {unreviewedCount > 0
            ? `${unreviewedCount} urgent item${unreviewedCount === 1 ? "" : "s"} to action`
            : totalItems > 0
              ? "All caught up — no pending reviews"
              : "Squad health check"}
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5">
          Persistent bloodwork deviations + active injuries, ranked by severity × persistence.
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onPrint}
          disabled={!unreviewedCount}
          className="pill disabled:opacity-40"
          data-testid="urgent-print-btn"
          title={unreviewedCount ? "Print the pending corrective actions" : "Nothing to print"}
        >
          <Printer size={13} /> Print urgent actions
        </button>
        <button onClick={onClose} className="pill !p-1.5" data-testid="urgent-close-btn">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// Status / severity → row accent colour. Pulled out of the JSX so the
// 5-branch waterfall doesn't read like a Christmas tree.
function rowAccentColor(item) {
  if (item.status === "high") return "#FB7185";
  if (item.status === "low") return "#FBBF24";
  if (item.severity === "see_doctor") return "#FB7185";
  if (item.severity === "rest_needed") return "#FB923C";
  return "#94A3B8";
}

// Per-datapoint badge classes for the "recent values" strip.
function valueBadgeClass(status) {
  if (status === "high") return "border-rose-500/50 text-rose-300 bg-rose-500/10";
  if (status === "low") return "border-amber-500/50 text-amber-300 bg-amber-500/10";
  return "border-slate-700 text-slate-400";
}

function UrgentRow({ rank, item, onToggle, saving }) {
  const isLab = item.kind === "lab";
  const isInjury = item.kind === "injury";
  const isScheduleLab = item.kind === "schedule_lab";
  const isScheduleDeworm = item.kind === "schedule_deworming";
  const isSchedule = isScheduleLab || isScheduleDeworm;
  const statusColor = rowAccentColor(item);
  // Compact type-pill config keyed off `kind`. Keeping the styling
  // co-located makes it obvious the four kinds render four ways —
  // previously we branched on isLab alone and every non-lab kind
  // fell through to "INJURY" (which then wrapped a deworming or
  // diagnostic reminder in the injury layout).
  let pill;
  if (isLab)              pill = <><FlaskConical size={9} className="inline -mt-0.5" /> LAB</>;
  else if (isInjury)      pill = <><Activity size={9} className="inline -mt-0.5" /> INJURY</>;
  else if (isScheduleLab) pill = <><CalendarClock size={9} className="inline -mt-0.5" /> DIAGNOSTIC</>;
  else                    pill = <><CalendarClock size={9} className="inline -mt-0.5" /> DEWORMING</>;
  const pillClass = isLab      ? "bg-amber-500/15 text-amber-300 border-amber-500/35"
                  : isInjury   ? "bg-rose-500/15 text-rose-300 border-rose-500/35"
                              : "bg-cyan-500/15 text-cyan-300 border-cyan-500/35";
  return (
    <div
      className={`rounded-lg border p-3 transition-opacity ${
        item.reviewed
          ? "border-emerald-500/30 bg-emerald-500/[0.05] opacity-60"
          : "border-[#1E304B] bg-[#0B1627]"
      }`}
      data-testid={`urgent-row-${item.issue_key}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onToggle}
          disabled={saving}
          className={`shrink-0 mt-0.5 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
            item.reviewed
              ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-300"
              : "border-slate-500 hover:border-cyan-400 hover:bg-cyan-500/10"
          }`}
          aria-label={item.reviewed ? "Mark as not reviewed" : "Mark as reviewed"}
          data-testid={`urgent-check-${item.issue_key}`}
        >
          {saving ? <Loader2 className="animate-spin" size={12} /> : item.reviewed && <CheckCircle2 size={14} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-[10px] font-mono-data text-slate-500">#{rank}</span>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-mono-data border ${pillClass}`}>
              {pill}
            </span>
            <span className="font-display text-slate-100 text-sm font-semibold">{item.sailor_name}</span>
            <span className="text-[10px] text-slate-500">· {item.sailor_group}</span>
            <span
              className="ml-auto text-[10px] font-mono-data px-1.5 py-0.5 rounded"
              style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}55` }}
              data-testid={`urgent-score-${item.issue_key}`}
            >
              score {item.score}
            </span>
          </div>

          {isLab ? <LabBody item={item} />
            : isInjury ? <InjuryBody item={item} />
            : <ScheduleBody item={item} />}

          <div className="mt-2 p-2 rounded border border-cyan-500/25 bg-cyan-500/[0.06]">
            <div className="text-[9px] uppercase tracking-widest text-cyan-300/90 font-mono-data mb-1">
              Suggested corrective action
            </div>
            <div className="text-[12px] text-slate-200 leading-snug">{item.corrective_action}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LabBody({ item }) {
  const range = `${item.range_low ?? "—"} — ${item.range_high ?? "—"} ${item.param_unit || ""}`.trim();
  return (
    <>
      <div className="mt-1.5 text-[12px] text-slate-200">
        <span className="font-semibold">{item.param_label}</span>{" "}
        <span className="text-slate-500">({item.param_group})</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] font-mono-data">
        <span className="text-slate-500">Recent values:</span>
        {(item.last_values || []).map((v, i) => (
          <span
            key={`${v.date}-${i}`}
            className={`px-1.5 py-0.5 rounded border ${valueBadgeClass(v.status)}`}
            title={fmtDate(v.date)}
          >
            {v.value} <span className="text-[9px] opacity-70">· {(v.date || "").slice(2, 10)}</span>
          </span>
        ))}
        <ChevronRight size={11} className="text-slate-700" />
        <span className="text-slate-500">range</span>
        <span className="text-slate-300">{range}</span>
        <span className="text-slate-500">·</span>
        <span className="text-rose-300">persistence {item.persistence}× · {Math.abs(item.deviation_pct)}% over edge</span>
      </div>
    </>
  );
}

function InjuryBody({ item }) {
  const bodyPart =
    item.body_part === "general" && item.body_part_other
      ? item.body_part_other
      : (item.body_part || "");
  const typeStr =
    item.injury_type === "other" && item.injury_type_other
      ? item.injury_type_other
      : (item.injury_type || "");
  return (
    <>
      <div className="mt-1.5 text-[12px] text-slate-200">
        <span className="font-semibold capitalize">{typeStr.replace(/_/g, " ")}</span>
        {bodyPart && <> · <span className="text-slate-300">{bodyPart.replace(/_/g, " ")}</span></>}
        {item.severity && (
          <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/35 font-mono-data">
            {item.severity.replace(/_/g, " ")}
          </span>
        )}
      </div>
      {item.description && (
        <div className="mt-1 text-[11px] text-slate-400 italic line-clamp-2">{item.description}</div>
      )}
      <div className="mt-1 text-[11px] font-mono-data text-slate-500">
        Onset {fmtDate(item.onset_date)} · {item.days_active} days active · status {item.status}
      </div>
    </>
  );
}

function ScheduleBody({ item }) {
  const days = item.days_until ?? 0;
  const isOverdue = days < 0;
  const urgencyBadgeClass =
    item.urgency === "red" ? "bg-rose-500/15 text-rose-300 border-rose-500/35"
    : item.urgency === "orange" ? "bg-amber-500/15 text-amber-300 border-amber-500/35"
    : "bg-yellow-500/15 text-yellow-300 border-yellow-500/35";
  return (
    <>
      <div className="mt-1.5 text-[12px] text-slate-200">
        <span className="font-semibold">{item.label}</span>
        {item.urgency && (
          <span className={`ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-mono-data ${urgencyBadgeClass}`}>
            {item.urgency}
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] font-mono-data text-slate-500">
        Due {fmtDate(item.due_date)} ·{" "}
        {isOverdue
          ? <span className="text-rose-300">overdue by {Math.abs(days)} day{Math.abs(days) === 1 ? "" : "s"}</span>
          : <span className="text-slate-400">in {days} day{days === 1 ? "" : "s"}</span>}
      </div>
    </>
  );
}
